import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  GraduationCap, Search, MapPin, TrendingUp, ExternalLink, 
  Filter, X, ChevronLeft, ChevronRight, Globe, DollarSign,
  Building2, BookOpen, Loader2
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

// Currency conversion rates (approximate - can be fetched from API)
const CURRENCY_RATES = {
  "US": { code: "USD", symbol: "$", rate: 1 },
  "IN": { code: "INR", symbol: "₹", rate: 83.5 },
  "GB": { code: "GBP", symbol: "£", rate: 0.79 },
  "EU": { code: "EUR", symbol: "€", rate: 0.92 },
  "AU": { code: "AUD", symbol: "A$", rate: 1.53 },
  "CA": { code: "CAD", symbol: "C$", rate: 1.36 },
  "JP": { code: "JPY", symbol: "¥", rate: 149 },
  "CN": { code: "CNY", symbol: "¥", rate: 7.24 },
  "DE": { code: "EUR", symbol: "€", rate: 0.92 },
  "FR": { code: "EUR", symbol: "€", rate: 0.92 },
  "SG": { code: "SGD", symbol: "S$", rate: 1.34 },
  "AE": { code: "AED", symbol: "د.إ", rate: 3.67 },
  "SA": { code: "SAR", symbol: "﷼", rate: 3.75 },
  "MY": { code: "MYR", symbol: "RM", rate: 4.47 },
  "TH": { code: "THB", symbol: "฿", rate: 35.5 },
  "PH": { code: "PHP", symbol: "₱", rate: 56 },
  "ID": { code: "IDR", symbol: "Rp", rate: 15700 },
  "BR": { code: "BRL", symbol: "R$", rate: 4.97 },
  "MX": { code: "MXN", symbol: "$", rate: 17.1 },
  "ZA": { code: "ZAR", symbol: "R", rate: 18.7 },
  "KR": { code: "KRW", symbol: "₩", rate: 1330 },
  "HK": { code: "HKD", symbol: "HK$", rate: 7.82 },
  "NZ": { code: "NZD", symbol: "NZ$", rate: 1.64 },
  "CH": { code: "CHF", symbol: "Fr", rate: 0.88 },
  "SE": { code: "SEK", symbol: "kr", rate: 10.4 },
  "NO": { code: "NOK", symbol: "kr", rate: 10.6 },
  "DK": { code: "DKK", symbol: "kr", rate: 6.87 },
  "PL": { code: "PLN", symbol: "zł", rate: 4.0 },
  "RU": { code: "RUB", symbol: "₽", rate: 92 },
  "IL": { code: "ILS", symbol: "₪", rate: 3.7 },
  "EG": { code: "EGP", symbol: "E£", rate: 30.9 },
  "NG": { code: "NGN", symbol: "₦", rate: 1550 },
  "KE": { code: "KES", symbol: "KSh", rate: 154 },
  "NA": { code: "NAD", symbol: "N$", rate: 18.7 },
};

const Colleges = () => {
  const { token } = useContext(AuthContext);
  const { t } = useLanguage();
  
  // Data states
  const [colleges, setColleges] = useState([]);
  const [countries, setCountries] = useState([]);
  const [courses, setCourses] = useState([]);
  const [types, setTypes] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedInfoType, setSelectedInfoType] = useState(""); // all, verified, basic
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalColleges, setTotalColleges] = useState(0);
  const [pageSize] = useState(30);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch filters on mount
  useEffect(() => {
    fetchFilters();
  }, []);

  // Fetch colleges when filters change
  useEffect(() => {
    fetchColleges();
  }, [debouncedSearch, selectedCountry, selectedCourse, selectedType, selectedInfoType, currentPage]);

  const fetchFilters = async () => {
    try {
      const [countriesRes, coursesRes, typesRes] = await Promise.all([
        axios.get(`${API}/colleges/countries/list`),
        axios.get(`${API}/colleges/courses/list`),
        axios.get(`${API}/colleges/types/list`)
      ]);
      setCountries(countriesRes.data.countries || []);
      setCourses(coursesRes.data.courses || []);
      setTypes(typesRes.data.types || []);
    } catch (error) {
      console.error("Error loading filters:", error);
    }
  };

  const fetchColleges = async () => {
    if (currentPage === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    
    try {
      const params = new URLSearchParams();
      params.append("page", currentPage);
      params.append("limit", pageSize);
      
      if (debouncedSearch) params.append("search", debouncedSearch);
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedCourse) params.append("course", selectedCourse);
      if (selectedType) params.append("type", selectedType);
      if (selectedInfoType === "verified") params.append("verified_only", "true");
      if (selectedInfoType === "basic") params.append("basic_only", "true");
      
      const response = await axios.get(`${API}/colleges?${params.toString()}`);
      
      setColleges(response.data.colleges || []);
      setTotalColleges(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error("Error loading colleges:", error);
      toast.error("Failed to load colleges");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCountry("");
    setSelectedCourse("");
    setSelectedType("");
    setSelectedInfoType("");
    setCurrentPage(1);
  };

  const hasFilters = searchTerm || selectedCountry || selectedCourse || selectedType || selectedInfoType;

  // Format fee with dual currency
  const formatFee = (fees, countryCode) => {
    if (!fees) return null;
    
    const ugFee = fees.undergraduate;
    const pgFee = fees.postgraduate;
    
    if (!ugFee && !pgFee) return null;
    
    const currency = CURRENCY_RATES[countryCode] || CURRENCY_RATES["US"];
    
    return { ugFee, pgFee, currency };
  };

  // Get country flag emoji
  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode.length !== 2) return "🌍";
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt(0));
    return String.fromCodePoint(...codePoints);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <Navbar />
        <div className="flex flex-col items-center justify-center h-96 gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-500" />
          <p className="text-gray-600 dark:text-gray-300">Loading universities...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8" data-testid="colleges-container">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-3">
            <GraduationCap className="w-8 h-8 md:w-10 md:h-10 text-indigo-500" />
            Universities & Colleges Worldwide
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Explore {totalColleges.toLocaleString()} universities from {countries.length} countries
          </p>
        </div>

        {/* Filters Section */}
        <Card className="mb-6 border-0 shadow-lg">
          <CardContent className="p-4 md:p-6">
            {/* Search Bar */}
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by name, country, or city..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-12"
                  data-testid="college-search-input"
                />
              </div>
              <Button 
                variant="outline" 
                className="md:hidden h-12"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters {hasFilters && `(${[selectedCountry, selectedCourse, selectedType].filter(Boolean).length})`}
              </Button>
            </div>

            {/* Desktop Filters */}
            <div className="hidden md:flex flex-wrap gap-4">
              {/* Country Filter */}
              <div className="min-w-[200px]">
                <label className="text-sm text-gray-500 mb-1 block">
                  <Globe className="w-4 h-4 inline mr-1" />
                  Country
                </label>
                <select
                  value={selectedCountry}
                  onChange={(e) => { setSelectedCountry(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  data-testid="country-filter"
                >
                  <option value="">All Countries ({countries.length})</option>
                  {countries.map(c => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* Course Filter */}
              <div className="min-w-[200px]">
                <label className="text-sm text-gray-500 mb-1 block">
                  <BookOpen className="w-4 h-4 inline mr-1" />
                  Course
                </label>
                <select
                  value={selectedCourse}
                  onChange={(e) => { setSelectedCourse(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  data-testid="course-filter"
                >
                  <option value="">All Courses</option>
                  {courses.slice(0, 50).map(c => (
                    <option key={c.name} value={c.name}>
                      {c.name} ({c.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div className="min-w-[180px]">
                <label className="text-sm text-gray-500 mb-1 block">
                  <Building2 className="w-4 h-4 inline mr-1" />
                  Type
                </label>
                <select
                  value={selectedType}
                  onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  data-testid="type-filter"
                >
                  <option value="">All Types</option>
                  {types.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Info Type Filter */}
              <div className="min-w-[150px]">
                <label className="text-sm text-gray-500 mb-1 block">
                  Data Status
                </label>
                <select
                  value={selectedInfoType}
                  onChange={(e) => { setSelectedInfoType(e.target.value); setCurrentPage(1); }}
                  className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  data-testid="info-type-filter"
                >
                  <option value="">All Universities</option>
                  <option value="verified">✓ Verified Only</option>
                  <option value="basic">Basic Info Only</option>
                </select>
              </div>

              {hasFilters && (
                <div className="flex items-end">
                  <Button variant="ghost" onClick={clearFilters} className="h-10">
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile Filters */}
            {showFilters && (
              <div className="md:hidden space-y-4 mt-4 pt-4 border-t">
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Country</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => { setSelectedCountry(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  >
                    <option value="">All Countries</option>
                    {countries.map(c => (
                      <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Course</label>
                  <select
                    value={selectedCourse}
                    onChange={(e) => { setSelectedCourse(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  >
                    <option value="">All Courses</option>
                    {courses.slice(0, 50).map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-500 mb-1 block">Type</label>
                  <select
                    value={selectedType}
                    onChange={(e) => { setSelectedType(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                  >
                    <option value="">All Types</option>
                    {types.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                {hasFilters && (
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    <X className="w-4 h-4 mr-2" />
                    Clear All Filters
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Info */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalColleges)} of {totalColleges.toLocaleString()} universities
            </p>
            <div className="hidden sm:flex items-center gap-2 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Verified
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                Basic Info
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </p>
        </div>

        {/* Colleges Grid */}
        {loadingMore ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : colleges.length === 0 ? (
          <div className="text-center py-16" data-testid="no-colleges-message">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-4">No universities found</p>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {colleges.map((college) => {
              const feeInfo = formatFee(college.fees, college.country_code);
              
              return (
                <Card 
                  key={college.id} 
                  className="overflow-hidden hover:shadow-xl transition-all duration-300 border-0 shadow-md"
                  data-testid={`college-card-${college.id}`}
                >
                  {/* College Header */}
                  <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4 text-white">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg leading-tight line-clamp-2">
                          {college.name}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-white/90 text-sm">
                          <span className="text-lg">{getCountryFlag(college.country_code)}</span>
                          <span className="truncate">{college.country}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {college.ranking?.qs_world && (
                          <Badge className="bg-white/20 text-white border-0 shrink-0">
                            QS #{college.ranking.qs_world}
                          </Badge>
                        )}
                        {/* Verified / Basic Info Badge */}
                        {college.is_basic_info_only ? (
                          <Badge className="bg-yellow-500/80 text-white border-0 text-xs">
                            Basic Info
                          </Badge>
                        ) : (
                          <Badge className="bg-green-500/80 text-white border-0 text-xs">
                            ✓ Verified
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <CardContent className="p-4 space-y-3">
                    {/* Location */}
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-sm">
                      <MapPin className="w-4 h-4 shrink-0" />
                      <span className="truncate">{college.location || `${college.city || ''}, ${college.state || ''}`}</span>
                    </div>

                    {/* Type Badge */}
                    <Badge variant="outline" className="text-xs">
                      {college.type || "University"}
                    </Badge>

                    {/* For Basic Info colleges - show website prominently */}
                    {college.is_basic_info_only && college.website && (
                      <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Official Website:</p>
                        <a 
                          href={college.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline truncate block"
                        >
                          {college.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      </div>
                    )}

                    {/* Courses - only for verified colleges */}
                    {!college.is_basic_info_only && college.courses_offered && college.courses_offered.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-gray-500 mb-1">Courses:</p>
                        <div className="flex flex-wrap gap-1">
                          {college.courses_offered.slice(0, 2).map((course, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {course.length > 20 ? course.substring(0, 20) + '...' : course}
                            </Badge>
                          ))}
                          {college.courses_offered.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{college.courses_offered.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Fees - Dual Currency - only for verified */}
                    {!college.is_basic_info_only && feeInfo && (feeInfo.ugFee || feeInfo.pgFee) && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-1 flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          Fees (Annual)
                        </p>
                        {feeInfo.ugFee && feeInfo.ugFee !== "N/A" && (
                          <p className="text-xs text-blue-800 dark:text-blue-300">
                            UG: {feeInfo.ugFee}
                          </p>
                        )}
                        {feeInfo.pgFee && feeInfo.pgFee !== "N/A" && (
                          <p className="text-xs text-blue-800 dark:text-blue-300">
                            PG: {feeInfo.pgFee}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Placements - only for verified */}
                    {!college.is_basic_info_only && college.placements?.average_package && (
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                        <p className="text-xs font-semibold text-green-900 dark:text-green-200 mb-1">
                          Placements
                        </p>
                        <p className="text-xs text-green-800 dark:text-green-300">
                          Avg: {college.placements.average_package}
                        </p>
                        {college.placements.placement_rate && (
                          <p className="text-xs text-green-800 dark:text-green-300">
                            Rate: {college.placements.placement_rate}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 pt-2">
                      <Button 
                        className="flex-1" 
                        size="sm"
                        onClick={() => window.location.href = `/college/${college.id}`}
                      >
                        View Details
                      </Button>
                      {college.website && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={college.website} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-8">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Previous
            </Button>
            
            <div className="flex items-center gap-2">
              {/* First page */}
              {currentPage > 3 && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => setCurrentPage(1)}>1</Button>
                  {currentPage > 4 && <span className="text-gray-400">...</span>}
                </>
              )}
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                if (pageNum < 1 || pageNum > totalPages) return null;
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              {/* Last page */}
              {currentPage < totalPages - 2 && totalPages > 5 && (
                <>
                  {currentPage < totalPages - 3 && <span className="text-gray-400">...</span>}
                  <Button variant="ghost" size="sm" onClick={() => setCurrentPage(totalPages)}>
                    {totalPages}
                  </Button>
                </>
              )}
            </div>
            
            <Button
              variant="outline"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              data-testid="next-page-btn"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Colleges;
